import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "visitorConsentRecord";
const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const CONSENT_VERSION = "1.0";

function generateUuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function parseBrowserInfo(ua = "") {
  const userAgent = ua.toLowerCase();
  let browserName = "Unknown";
  let browserVersion = "";
  let osName = "Unknown";

  if (/edg\//.test(userAgent)) {
    browserName = "Edge";
    browserVersion = (ua.match(/Edg\/(\d+[\.\d]*)/) || [])[1] || "";
  } else if (/chrome\//.test(userAgent) && !/edg\//.test(userAgent) && !/opr\//.test(userAgent)) {
    browserName = "Chrome";
    browserVersion = (ua.match(/Chrome\/(\d+[\.\d]*)/) || [])[1] || "";
  } else if (/safari\//.test(userAgent) && !/chrome\//.test(userAgent)) {
    browserName = "Safari";
    browserVersion = (ua.match(/Version\/(\d+[\.\d]*)/) || [])[1] || "";
  } else if (/firefox\//.test(userAgent)) {
    browserName = "Firefox";
    browserVersion = (ua.match(/Firefox\/(\d+[\.\d]*)/) || [])[1] || "";
  } else if (/opr\//.test(userAgent) || /opera\//.test(userAgent)) {
    browserName = "Opera";
    browserVersion = (ua.match(/(OPR|Opera)\/(\d+[\.\d]*)/) || [])[2] || "";
  }

  if (/windows nt/.test(userAgent)) {
    osName = "Windows";
  } else if (/mac os x/.test(userAgent)) {
    osName = "macOS";
  } else if (/android/.test(userAgent)) {
    osName = "Android";
  } else if (/iphone|ipad|ipod/.test(userAgent)) {
    osName = "iOS";
  } else if (/linux/.test(userAgent)) {
    osName = "Linux";
  }

  return { browserName, browserVersion, osName };
}

function getStoredConsent() {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function saveLocalConsent(record) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
}

async function saveConsentToBackend(payload) {
  if (!API_URL) return;

  await fetch(`${API_URL}/api/quote/visitor-consent`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export default function VisitorConsentDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const consentRecord = useMemo(() => getStoredConsent(), []);
  const deviceId = useMemo(() => {
    if (consentRecord?.device_id) {
      return consentRecord.device_id;
    }
    return generateUuid();
  }, [consentRecord]);

  const browserInfo = useMemo(() => parseBrowserInfo(typeof navigator !== "undefined" ? navigator.userAgent : ""), []);
  const acceptLanguage = typeof navigator !== "undefined" ? navigator.language || "" : "";
  const sessionId = useMemo(() => {
    if (typeof window === "undefined") return generateUuid();
    const existing = window.sessionStorage.getItem("visitorConsentSessionId");
    if (existing) return existing;
    const newId = generateUuid();
    window.sessionStorage.setItem("visitorConsentSessionId", newId);
    return newId;
  }, []);

  useEffect(() => {
    if (!consentRecord) {
      setIsOpen(true);
    }
  }, [consentRecord]);

  const handleDecision = async (status) => {
    if (isSubmitted) return;
    setIsSaving(true);
    setError("");

    const payload = {
      device_id: deviceId,
      consent_status: status,
      browser_name: browserInfo.browserName,
      browser_version: browserInfo.browserVersion,
      os_name: browserInfo.osName,
      session_id: sessionId,
      consent_version: CONSENT_VERSION,
      accept_language: acceptLanguage,
    };

    try {
      await saveConsentToBackend(payload);
      const record = {
        ...payload,
        created_at: new Date().toISOString(),
      };
      saveLocalConsent(record);
      setIsSubmitted(true);
      setIsOpen(false);
    } catch (err) {
      setError("Unable to save consent right now. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-2xl rounded-3xl border border-white/10 bg-background p-6 shadow-2xl">
        <div className="space-y-4 text-left text-sm text-muted-foreground">
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-foreground dark:text-white">Visitor Consent</h2>
            <p>
              We use a visitor identifier to remember your preferences and keep your session consistent. Do you agree to let us collect minimal browser data to store your consent status and improve your experience?
            </p>
          </div>


          {error ? <div className="rounded-lg border border-red-300 dark:border-destructive/20 bg-red-50 dark:bg-destructive/10 px-4 py-3 text-sm text-red-700 dark:text-red-400">{error}</div> : null}

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              disabled={isSaving}
              onClick={() => handleDecision("rejected")}
            >
              Reject
            </Button>
            <Button
              className="w-full sm:w-auto"
              disabled={isSaving}
              onClick={() => handleDecision("accepted")}
            >
              Accept
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
